import { type ReactNode, useEffect, useId, useRef } from 'react';
import styles from './Dialog.module.css';
import { Icon } from './Icon';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Buttons aligned at the bottom right. */
  actions?: ReactNode;
  width?: number;
};

/** Native modal <dialog>: focus trapping, Esc and the top layer come from the browser. */
export function Dialog({ open, onClose, title, children, actions, width = 440 }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // React's autoFocus runs before the dialog is shown, so it cannot take focus:
      // fields that should start focused carry data-autofocus instead.
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      style={{ width }}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onMouseDown={(event) => {
        // A click on the backdrop lands on the <dialog> itself.
        if (event.target === ref.current) onClose();
      }}
    >
      {open ? (
        <div className={styles.panel}>
          <header className={styles.header}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            <button type="button" className={styles.close} onClick={onClose} aria-label="Chiudi">
              <Icon name="close" />
            </button>
          </header>
          <div className={styles.body}>{children}</div>
          {actions ? <footer className={styles.actions}>{actions}</footer> : null}
        </div>
      ) : null}
    </dialog>
  );
}
